<template>
  <div class="md:db-tw-grid md:db-tw-grid-cols-2 md:db-tw-gap-3">
    <div
      v-for="(date, index) in dateFields"
      :key="index"
      class="db-tw-mt-8"
    >
      <DbDatepicker
        :date="date"
        :minDate="subDays(new Date(), 0)"
        v-on:update:date="updateDate(index, $event)"
      >
        {{ label }} {{ index >= 1 ? index + 1 : '' }}
      </DbDatepicker>
      <DbButton
        :label="`${labelRemove} ${index >= 1 ? index + 1 : ''}`"
        :icon="removeIcon"
        v-on:click-action="removeDate(index)"
      />
    </div>
  </div>
  <DbButton
    v-if="canAddField"
    :label="`${labelAdd} ${nbFields >= 1 ? nbFields + 1 : ''}`"
    :icon="addIcon"
    v-on:click-action="addField"
  />
</template>

<script setup lang="ts">
import { computed, ref, watchEffect } from 'vue'
import { subDays } from 'date-fns'
import IconDbPlus from '~icons/db/plus'
import IconDbMinus from '~icons/db/minus'

/*
TODO: accessibility not done yet,
the focus is not re-assigned to the correct elements
when adding / removing them
*/

const props = defineProps<{
  modelValue: Date[]
  limit: number
  label: string
  /** Text for the button "Add" */
  labelAdd: string
  /** Text for the button "Remove" */
  labelRemove: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', dates: Date[]): void
}>()

const addIcon = { icon: IconDbPlus, class: 'db-text-color-supporting' }
const removeIcon = { icon: IconDbMinus, class: 'db-text-color-red' }

/** Internal state of the component with dates / empty dates */
const dateFields = ref<(Date | undefined)[]>([])

const nbFields = computed(() => {
  return dateFields.value.length
})

/** If the button "Add" should be visible */
const canAddField = computed(() => {
  const hasReachedLimit = (props.limit && props.limit <= nbFields.value) || props.limit === 0

  if (hasReachedLimit) {
    return false
  }

  const lastValueIsNotValid = nbFields.value && dateFields.value[nbFields.value - 1] === undefined
  if (lastValueIsNotValid) {
    return false
  }
  return true
})

/** Add an empty date field */
const addField = () => {
  dateFields.value.push(undefined)
}

/**
 * Ensure Typescript type guarding when using .filter()
 * because Typescript has several overload for Array.filter
 * by created a dedicated function it is easier for Typescript
 * to do typing than by doing .filter(date => date !== undefined)
 */
function isDate(argument: Date | undefined): argument is Date {
  return argument !== undefined
}

/** When props change, we need to refect our list of fields */
watchEffect(() => {
  dateFields.value = props.modelValue
})

const removeDate = (index: number) => {
  dateFields.value.splice(index, 1)
  emit('update:modelValue', dateFields.value.filter(isDate))
}

const updateDate = (index: number, date: Date | undefined) => {
  dateFields.value[index] = date
  emit('update:modelValue', dateFields.value.filter(isDate))
}
</script>
